import { useState } from 'react';
import { dataRepository } from '../repository/DataRepository';
import './SourceFormModal.css';

// Field definitions for each source type - matching mobile
const SOURCE_TYPE_FIELDS: Record<string, Array<{ name: string; label: string; type: 'text' | 'textarea' | 'date' | 'number' | 'url' }>> = {
  film: [
    { name: 'director', label: 'Director', type: 'text' },
    { name: 'year', label: 'Year', type: 'text' },
    { name: 'genre', label: 'Genre', type: 'text' },
    { name: 'runtime', label: 'Runtime (min)', type: 'number' },
    { name: 'imdbUrl', label: 'IMDB URL', type: 'url' },
  ],
  podcast: [
    { name: 'podcastName', label: 'Podcast Name', type: 'text' },
    { name: 'host', label: 'Host', type: 'text' },
    { name: 'episodeNumber', label: 'Episode #', type: 'text' },
    { name: 'episodeDate', label: 'Episode Date', type: 'date' },
    { name: 'duration', label: 'Duration', type: 'text' },
    { name: 'podcastUrl', label: 'Episode URL', type: 'url' },
  ],
  article: [
    { name: 'author', label: 'Author', type: 'text' },
    { name: 'publication', label: 'Publication', type: 'text' },
    { name: 'publishDate', label: 'Publish Date', type: 'date' },
    { name: 'articleUrl', label: 'URL', type: 'url' },
  ],
  video: [
    { name: 'creator', label: 'Creator/Channel', type: 'text' },
    { name: 'platform', label: 'Platform', type: 'text' },
    { name: 'uploadDate', label: 'Upload Date', type: 'date' },
    { name: 'duration', label: 'Duration', type: 'text' },
    { name: 'videoUrl', label: 'Video URL', type: 'url' },
  ],
  quote: [
    { name: 'quoteText', label: 'The Quote', type: 'textarea' },
    { name: 'speaker', label: 'Who Said It', type: 'text' },
    { name: 'context', label: 'Context/Source', type: 'text' },
    { name: 'quoteDate', label: 'Date (if known)', type: 'text' },
  ],
  other: [
    { name: 'author', label: 'Author/Creator', type: 'text' },
    { name: 'sourceUrl', label: 'URL', type: 'url' },
    { name: 'date', label: 'Date', type: 'date' },
    { name: 'notes', label: 'Notes', type: 'textarea' },
  ],
};

interface SourceFormModalProps {
  sourceTypeId: string;
  sourceTypeLabel: string;
  sourceTypeIcon: string;
  onClose: () => void;
  onCreated: (objectId: string) => void;
}

export default function SourceFormModal({
  sourceTypeId,
  sourceTypeLabel,
  sourceTypeIcon,
  onClose,
  onCreated,
}: SourceFormModalProps) {
  const fields = SOURCE_TYPE_FIELDS[sourceTypeId] || SOURCE_TYPE_FIELDS.other;

  const [title, setTitle] = useState('');
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateField = (name: string, value: string) => {
    setFieldValues(prev => ({ ...prev, [name]: value }));
  };

  const handleCreate = async () => {
    if (!title.trim()) {
      setError('Please enter a title');
      return;
    }

    try {
      setCreating(true);
      setError(null);

      // Create the source object
      const source = await dataRepository.createObject(title.trim(), 'source');

      // Set sourceType property
      await dataRepository.createProperty(source.id, 'sourceType', 'text', sourceTypeId);

      // Set all field values as properties
      for (const field of fields) {
        const value = fieldValues[field.name];
        if (value?.trim()) {
          await dataRepository.createProperty(
            source.id,
            field.name,
            field.type === 'number' ? 'number' : 'text',
            value.trim()
          );
        }
      }

      // Create info block on canvas based on source type
      const infoContent = buildInfoBlock(sourceTypeId, title, fieldValues);
      if (infoContent) {
        await dataRepository.createBlock({
          objectId: source.id,
          type: 'text',
          content: infoContent,
          x: 50,
          y: 50,
          width: 400,
          height: 200,
          zIndex: 1,
          metadata: { sourceInfo: true, sourceType: sourceTypeId },
        });
      }

      // For quotes, create a special quote block
      if (sourceTypeId === 'quote' && fieldValues.quoteText) {
        await dataRepository.createBlock({
          objectId: source.id,
          type: 'text',
          content: `<blockquote style="font-size: 1.2em; font-style: italic; border-left: 3px solid #3b82f6; padding-left: 16px; margin: 0;">
            "${fieldValues.quoteText}"
          </blockquote>
          ${fieldValues.speaker ? `<p style="text-align: right; color: #888;">— ${fieldValues.speaker}</p>` : ''}`,
          x: 50,
          y: 50,
          width: 500,
          height: 150,
          zIndex: 1,
          metadata: { quoteBlock: true },
        });
      }

      onCreated(source.id);
    } catch (err) {
      console.error('Error creating source:', err);
      setError('Failed to create source. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  // Build HTML content for info block based on source type
  const buildInfoBlock = (type: string, title: string, values: Record<string, string>): string => {
    switch (type) {
      case 'film':
        return `<p><strong>${sourceTypeIcon} ${title}</strong></p>
          ${values.director ? `<p>Directed by ${values.director}</p>` : ''}
          ${values.year ? `<p>${values.year}${values.runtime ? ` · ${values.runtime} min` : ''}</p>` : ''}
          ${values.genre ? `<p style="color: #888">${values.genre}</p>` : ''}`;

      case 'podcast':
        return `<p><strong>${sourceTypeIcon} ${title}</strong></p>
          ${values.podcastName ? `<p>${values.podcastName}</p>` : ''}
          ${values.host ? `<p>Host: ${values.host}</p>` : ''}
          ${values.episodeNumber ? `<p>Episode ${values.episodeNumber}${values.episodeDate ? ` · ${values.episodeDate}` : ''}</p>` : ''}
          ${values.duration ? `<p style="color: #888">${values.duration}</p>` : ''}`;

      case 'article':
        return `<p><strong>${sourceTypeIcon} ${title}</strong></p>
          ${values.author ? `<p>by ${values.author}</p>` : ''}
          ${values.publication ? `<p>${values.publication}${values.publishDate ? ` · ${values.publishDate}` : ''}</p>` : ''}
          ${values.articleUrl ? `<p><a href="${values.articleUrl}">${values.articleUrl}</a></p>` : ''}`;

      case 'video':
        return `<p><strong>${sourceTypeIcon} ${title}</strong></p>
          ${values.creator ? `<p>${values.creator}</p>` : ''}
          ${values.platform ? `<p>${values.platform}${values.uploadDate ? ` · ${values.uploadDate}` : ''}</p>` : ''}
          ${values.duration ? `<p style="color: #888">${values.duration}</p>` : ''}`;

      case 'quote':
        // Quote has its own special block, return empty
        return '';

      default:
        return `<p><strong>${sourceTypeIcon} ${title}</strong></p>
          ${values.author ? `<p>by ${values.author}</p>` : ''}
          ${values.date ? `<p>${values.date}</p>` : ''}
          ${values.notes ? `<p style="color: #888">${values.notes}</p>` : ''}`;
    }
  };

  return (
    <div className="source-form-overlay" onClick={onClose}>
      <div className="source-form-modal" onClick={e => e.stopPropagation()}>
        <div className="source-form-header">
          <h2>{sourceTypeIcon} New {sourceTypeLabel}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {error && (
          <div className="source-form-error">
            {error}
          </div>
        )}

        <div className="source-form-content">
          {/* Title field */}
          <div className="source-form-field">
            <label>Title *</label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder={`Enter ${sourceTypeLabel.toLowerCase()} title...`}
              autoFocus
            />
          </div>

          {/* Source type specific fields */}
          {fields.map(field => (
            <div key={field.name} className="source-form-field">
              <label>{field.label}</label>
              {field.type === 'textarea' ? (
                <textarea
                  value={fieldValues[field.name] || ''}
                  onChange={e => updateField(field.name, e.target.value)}
                  placeholder={`Enter ${field.label.toLowerCase()}...`}
                  rows={4}
                />
              ) : (
                <input
                  type={field.type === 'url' ? 'url' : field.type === 'date' ? 'date' : field.type === 'number' ? 'number' : 'text'}
                  value={fieldValues[field.name] || ''}
                  onChange={e => updateField(field.name, e.target.value)}
                  placeholder={`Enter ${field.label.toLowerCase()}...`}
                />
              )}
            </div>
          ))}
        </div>

        <div className="source-form-footer">
          <button className="cancel-btn" onClick={onClose} disabled={creating}>
            Cancel
          </button>
          <button
            className="create-btn"
            onClick={handleCreate}
            disabled={!title.trim() || creating}
          >
            {creating ? 'Creating...' : `Create ${sourceTypeLabel}`}
          </button>
        </div>
      </div>
    </div>
  );
}
